/**
 * Learning editor model: the three screens, the draft the engineer builds and the preview of what would be learned.
 * Pure functions only (no React, no network), so every screen state is testable. Plan: Documentation/LEARNING_EDITOR_PLAN.md.
 * What a lesson must contain, and how a draft is cleaned, is in editorRules.js.
 */
import { FIX_KINDS, MAX_LIST_ITEMS, cleanDraft, firstUnmet, withBlankRow } from "./editorRules";
import { emptyBaseline, mergeTouched, offeredBaseline } from "./mergePrefill";
import {
  MATCH_STATUS, blockingIssues, idleMatch, loadingMatch, matchKey, matchPayload, metaFields, pickedMatch, resolvedMatch,
} from "./matchModel";

export const STEP = Object.freeze({ FORM: "form", PREVIEW: "preview", RESULT: "result" });
export const RESULT = Object.freeze({ LEARNED: "learned", REVIEW: "review", REFUSED: "refused" });
export const REFUSAL = Object.freeze({ LEARNING_OFF: "learning_off", ALREADY_TAUGHT: "already_taught", NO_DIAGNOSIS: "no_diagnosis" });
export const LIST = Object.freeze({ SYMPTOMS: "symptoms", STEPS: "steps" });

export const ACTION = Object.freeze({
  SET_ROOT_CAUSE: "set_root_cause",
  SET_FIX_KIND: "set_fix_kind",
  SET_FIX_TEXT: "set_fix_text",
  SET_LIST_ITEM: "set_list_item",
  ADD_LIST_ITEM: "add_list_item",
  REMOVE_LIST_ITEM: "remove_list_item",
  TO_PREVIEW: "to_preview",
  BACK: "back",
  SUBMIT_START: "submit_start",
  SHOW_RESULT: "show_result",
  SUBMIT_FAILED: "submit_failed",
  SUBMIT_HELD: "submit_held", // the save was not sent, or the server asked to wait: back on the preview, no error
  MATCH_START: "match_start", // retry after a failed match
  MATCH_RESOLVED: "match_resolved",
  MATCH_FAILED: "match_failed",
  PICK_MATCH: "pick_match",
  APPLY_PREFILL: "apply_prefill", // the server's prefill arrived (or the editor reopened with newer lines)
});

// Position of a screen in the step bar (TEXT.steps): Teach, Preview, Done.
const STEP_INDEX = Object.freeze({ [STEP.FORM]: 0, [STEP.PREVIEW]: 1, [STEP.RESULT]: 2 });
export const stepIndexOf = (step) => STEP_INDEX[step] ?? 0;

const NO_TRUNCATION = Object.freeze({ symptoms: 0, steps: 0 });
const cap = (items) => (items || []).slice(0, MAX_LIST_ITEMS);

/** The draft part of a state from a session: lists capped and given a blank row, provenance by exact text, root cause as given. */
const draftFrom = (session, previous = {}) => ({
  symptoms: withBlankRow(cap(session.symptoms)),
  steps: withBlankRow(cap(session.steps)),
  lineMeta: { ...(session.lineMeta || {}) },
  stepMeta: { ...(session.stepMeta || {}) },
  rootCause: String(session.rootCause || ""),
  fixKind: FIX_KINDS.includes(session.fixKind) ? session.fixKind : (previous.fixKind || ""),
  fixText: String(session.fixText || previous.fixText || ""),
});

/**
 * `session` is {category: {key, label}, symptoms: string[], steps: string[], lineMeta?, stepMeta?, truncated?: {symptoms, steps},
 * rootCause?: string, fixKind?: string, fixText?: string}: what the chat already knows, which the engineer edits. The fix starts
 * empty unless the caller passed it. `saved` is the draft kept from an earlier opening ({draft, baseline, truncated, category}):
 * it wins, and only the lines of `session` it never offered are added to it.
 */
export function initialState(session = {}, saved = null) {
  const base = {
    category: session.category || null,
    baseline: offeredBaseline(emptyBaseline(), session),
    truncated: { ...NO_TRUNCATION, ...(session.truncated || {}) },
    touched: false, // the engineer has changed the draft: a later prefill is then merged, never replacing
    step: STEP.FORM,
    preview: null,
    matchCache: null, // the last resolved match: Back then Review with an unchanged draft does not ask again
    result: null,
    saving: false,
    error: false,
  };
  if (!saved) return { ...base, draft: draftFrom(session) };
  const kept = {
    ...base,
    category: saved.category || base.category,
    baseline: saved.baseline,
    truncated: { ...NO_TRUNCATION, ...(saved.truncated || {}) },
    touched: true,
    draft: { ...draftFrom({}), ...saved.draft, symptoms: withBlankRow(saved.draft.symptoms), steps: withBlankRow(saved.draft.steps) },
  };
  const merged = mergeTouched(kept, session);
  return { ...kept, draft: { ...merged.draft, symptoms: withBlankRow(merged.draft.symptoms), steps: withBlankRow(merged.draft.steps) }, baseline: merged.baseline };
}

/** What is worth keeping for a later reopening: the draft with its baseline, once the engineer has changed it. */
export const savedOf = (state) => (state.touched
  ? { draft: state.draft, baseline: state.baseline, truncated: state.truncated, category: state.category } : null);

/** Whether the screen's forward button is allowed, and the first requirement still unmet (`hint`, shown beside the button). */
export function gate(state) {
  if (state.step === STEP.RESULT) return { ok: true, hint: null };
  const cleaned = state.step === STEP.PREVIEW ? state.preview.draft : cleanDraft(state.draft).draft;
  const unmet = firstUnmet(state.category, cleaned);
  const match = state.step === STEP.PREVIEW ? state.preview.match : null;
  const matching = !!match && match.status === MATCH_STATUS.LOADING;
  const blocked = !!match && blockingIssues(match).length > 0;
  let hint = null;
  if (unmet) hint = unmet.key;
  else if (matching) hint = GATE_MATCHING;
  else if (blocked) hint = GATE_BLOCKED;
  return { ok: !unmet && !state.saving && !matching && !blocked, hint };
}

/** What the caller saves: the cleaned draft in the server's field names, plus the engineer's match choices when there are any. */
export function payloadOf(state) {
  const payload = {
    category: state.category ? state.category.key : null,
    symptoms: state.preview.draft.symptoms,
    resolution_steps: state.preview.draft.steps,
    root_cause: state.preview.draft.rootCause,
    fix_kind: state.preview.draft.fixKind,
    fix_text: state.preview.draft.fixText,
    ...metaFields(state.preview.draft),
  };
  const match = matchPayload(state.preview.match);
  return match ? { ...payload, match } : payload;
}

// The hint keys gate() adds to the requirement keys of editorRules.
export const GATE_MATCHING = "matching";
export const GATE_BLOCKED = "blocking";

const onMatch = (state, key, change) => {
  const match = state.step === STEP.PREVIEW ? state.preview.match : null;
  if (!match || match.key !== key) return state;
  const next = change(match);
  return next === match ? state : { ...state, preview: { ...state.preview, match: next } };
};

const replaceAt = (list, index, value) => list.map((x, i) => (i === index ? value : x));
const META_KEY = Object.freeze({ symptoms: "lineMeta", steps: "stepMeta" });
const edited = (state, draft) => ({ ...state, touched: true, draft: { ...state.draft, ...draft } });
const setList = (state, list, items, meta) => edited(state, { [list]: items, ...(meta ? { [META_KEY[list]]: meta } : {}) });
/** The provenance map without `text` (an edited or removed line is the engineer's own words now), unless another row still has it. */
const withoutMeta = (state, list, text, items) => {
  const map = state.draft[META_KEY[list]] || {};
  if (!(text in map) || items.includes(text)) return map;
  const { [text]: _gone, ...rest } = map;
  return rest;
};
const isList = (list) => list === LIST.SYMPTOMS || list === LIST.STEPS;

/** A prefill that arrived after the editor opened: replaces an untouched form, is merged into a touched one. Never on Review. */
function applyPrefill(state, session) {
  if (!session || state.step !== STEP.FORM) return state;
  if (!state.touched) {
    const draft = draftFrom(session, state.draft);
    return {
      ...state, draft: { ...draft, rootCause: draft.rootCause || state.draft.rootCause },
      category: session.category || state.category, baseline: offeredBaseline(state.baseline, session),
      truncated: { ...NO_TRUNCATION, ...(session.truncated || {}) },
    };
  }
  const merged = mergeTouched(state, session);
  if (merged.draft === state.draft && merged.baseline === state.baseline) return state;
  return {
    ...state, baseline: merged.baseline,
    draft: { ...merged.draft, symptoms: withBlankRow(merged.draft.symptoms), steps: withBlankRow(merged.draft.steps) },
  };
}

export function reducer(state, action) {
  switch (action.type) {
    case ACTION.SET_ROOT_CAUSE:
      return edited(state, { rootCause: action.value });
    case ACTION.SET_FIX_KIND:
      return FIX_KINDS.includes(action.value) ? edited(state, { fixKind: action.value }) : state;
    case ACTION.SET_FIX_TEXT:
      return edited(state, { fixText: action.value });
    case ACTION.SET_LIST_ITEM: {
      if (!isList(action.list)) return state;
      const items = replaceAt(state.draft[action.list], action.index, action.value);
      return setList(state, action.list, items, withoutMeta(state, action.list, state.draft[action.list][action.index], items));
    }
    case ACTION.ADD_LIST_ITEM:
      return isList(action.list) && state.draft[action.list].length < MAX_LIST_ITEMS
        ? setList(state, action.list, [...state.draft[action.list], ""]) : state;
    case ACTION.REMOVE_LIST_ITEM: {
      if (!isList(action.list)) return state;
      const items = withBlankRow(state.draft[action.list].filter((_, i) => i !== action.index));
      return setList(state, action.list, items, withoutMeta(state, action.list, state.draft[action.list][action.index], items));
    }
    case ACTION.APPLY_PREFILL:
      return applyPrefill(state, action.session);
    case ACTION.TO_PREVIEW: {
      if (!(state.step === STEP.FORM && gate(state).ok)) return state;
      const cleaned = cleanDraft(state.draft);
      const key = matchKey(state.category, cleaned.draft);
      const cached = state.matchCache && state.matchCache.key === key ? state.matchCache : null;
      const match = !action.matching ? idleMatch() : cached || loadingMatch(key);
      return { ...state, step: STEP.PREVIEW, preview: { ...cleaned, match }, error: false };
    }
    case ACTION.BACK: {
      if (state.step !== STEP.PREVIEW) return state;
      const resolved = state.preview.match.status === MATCH_STATUS.READY;
      return { ...state, step: STEP.FORM, preview: null, result: null, error: false, matchCache: resolved ? state.preview.match : state.matchCache };
    }
    case ACTION.MATCH_START:
      return onMatch(state, action.key, (m) => (m.status === MATCH_STATUS.ERROR ? loadingMatch(m.key) : m));
    case ACTION.MATCH_RESOLVED:
      return onMatch(state, action.key, (m) => (m.status === MATCH_STATUS.LOADING ? resolvedMatch(m.key, action.data) : m));
    case ACTION.MATCH_FAILED:
      return onMatch(state, action.key, (m) => (m.status === MATCH_STATUS.LOADING ? { ...m, status: MATCH_STATUS.ERROR } : m));
    case ACTION.PICK_MATCH:
      return onMatch(state, state.preview ? state.preview.match.key : null, (m) => (m.status === MATCH_STATUS.READY ? pickedMatch(m, action) : m));
    case ACTION.SUBMIT_START:
      return state.step === STEP.PREVIEW && !state.saving ? { ...state, saving: true, error: false } : state;
    case ACTION.SHOW_RESULT:
      return state.saving ? { ...state, step: STEP.RESULT, saving: false, result: { kind: action.kind, reason: action.reason || null } } : state;
    case ACTION.SUBMIT_FAILED:
      return state.saving ? { ...state, saving: false, error: action.message || true } : state; // a string: the server's reason
    case ACTION.SUBMIT_HELD:
      return state.saving ? { ...state, saving: false, error: false } : state;
    default:
      return state;
  }
}
